import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ProductCard } from "../ProductCard";
import { ProductListNav } from "../ProductListNav";

import heroStyles from "../../styles/composable/product-list-hero.module.scss";
import productListStyles from "../../styles/composable/product-list.module.scss";
import styles from "./index.module.scss";

type IProps = Immutable<templates.ProductListPageTemplate>;

export const ProductListPageBody = (props: IProps): React.JSX.Element => {
    const alignment = props.page.hero_alignment;

    return (
        <>
            <div className="l-full-width">
                <div
                    className={concatClassNames([
                        "hero",
                        heroStyles.productListHero,
                        "l-full-width",
                        heroStyles.productListHeroTempurpedic,
                    ])}
                    style={
                        props.hero_image_url
                            ? {
                                  backgroundImage: `url(${props.hero_image_url})`,
                              }
                            : undefined
                    }
                >
                    <div className="l-capped-width">
                        {props.hero_badge_image_url && (
                            <div>
                                <div
                                    className={concatClassNames([
                                        heroStyles.productListHeroBadgeImageContainer,
                                        alignment === "left"
                                            ? heroStyles.productListHeroBadgeImageContainerLeft
                                            : null,
                                    ])}
                                >
                                    <img
                                        src={props.hero_badge_image_url}
                                        alt={props.hero_badge_image_alt}
                                        className={concatClassNames([
                                            heroStyles.productListHeroBadgeImage,
                                            "responsive-img",
                                        ])}
                                    />
                                </div>
                            </div>
                        )}
                        <div
                            className={concatClassNames([
                                heroStyles.productListHeroCopy,
                                heroStyles.productListHeroCopyTempurpedic,
                            ])}
                        >
                            {props.page.hero_category_title && (
                                <div
                                    className={concatClassNames([
                                        heroStyles.productListHeroCategory,
                                        heroStyles.productListHeroCategoryTempurpedic,
                                    ])}
                                >
                                    {props.page.hero_category_title}
                                </div>
                            )}
                            {props.page.hero_title && (
                                <div
                                    className={heroStyles.productListHeroTitle}
                                >
                                    {props.page.hero_title}
                                </div>
                            )}
                            {props.page.hero_description && (
                                <div
                                    className={concatClassNames([
                                        heroStyles.productListHeroDescription,
                                        heroStyles.productListHeroDescriptionTempurpedic,
                                    ])}
                                >
                                    {props.page.hero_description}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="product-lists u-margin-bottom--xl">
                <ProductListNav categories={props.categories} />

                {props.categories.map((category, i) => (
                    <div
                        key={i}
                        className={concatClassNames([
                            productListStyles.productList,
                            styles.productListTempurpedic,
                        ])}
                        id={category.title}
                    >
                        <div
                            className={concatClassNames([
                                styles.productListBackground,
                                "l-capped-width",
                            ])}
                        >
                            <div
                                className={concatClassNames([
                                    productListStyles.productListHeader,
                                    styles.productListHeaderTempurpedic,
                                ])}
                            >
                                <div
                                    className={concatClassNames([
                                        productListStyles.productListHeaderCopy,
                                        styles.productListHeaderCopyTempurpedic,
                                    ])}
                                >
                                    <div
                                        className={concatClassNames([
                                            productListStyles.productListTitle,
                                            "product-list__title",
                                        ])}
                                    >
                                        {category.title}
                                    </div>
                                    <div
                                        className={
                                            styles.productListDescription
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: category.description_html,
                                        }}
                                    />
                                </div>
                                {category.hero_image_url && (
                                    <img
                                        src={category.hero_image_url}
                                        alt={category.hero_image_alt}
                                        className="product-list__header-image responsive-img"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                )}
                            </div>
                            {category.products.length > 0 && (
                                <div
                                    className={concatClassNames([
                                        productListStyles.productListContent,
                                        "u-flex-container",
                                    ])}
                                >
                                    {category.products.map((product) => (
                                        <ProductCard
                                            key={product.id}
                                            product={product}
                                            className={
                                                productListStyles.productListCard
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                <div className="product-list-callout u-margin-bottom--xl">
                    {props.link_banners.map((banner, i) => (
                        <div key={i} className={styles.productListLinkBanner}>
                            <img
                                src={banner.image_url}
                                alt={banner.image_alt}
                                className={concatClassNames([
                                    styles.productListLinkBannerImage,
                                    "responsive-img",
                                ])}
                                loading="lazy"
                                decoding="async"
                            />
                            <div
                                className={concatClassNames([
                                    styles.productListLinkBannerCopy,
                                    "l-capped-width",
                                ])}
                            >
                                <div
                                    className={
                                        styles.productListLinkBannerTitle
                                    }
                                >
                                    {banner.text}
                                </div>
                                {banner.page_href && (
                                    <a
                                        href={banner.page_href}
                                        className={concatClassNames([
                                            styles.productListLinkBannerButton,
                                            "button",
                                            "button--primary-congress-blue",
                                        ])}
                                    >
                                        Shop {banner.page_title}
                                    </a>
                                )}
                                {banner.external_link && (
                                    <a
                                        href={banner.external_link}
                                        className={concatClassNames([
                                            styles.productListLinkBannerButton,
                                            "button",
                                            "button--primary-congress-blue",
                                        ])}
                                    >
                                        Shop Now
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};
